/**
 * One-shot sample playback: fetch, decode once, play many.
 *
 * This is the minimum useful slice of `design/gdd/audio.md`'s Phase 0 — enough
 * to let a real recorded cue replace a synthesized one, and no more. Two things
 * that document lists are deliberately **not** here yet, because nothing that
 * exists needs them:
 *
 * - **Variant rotation.** Needed by footsteps and impacts, which repeat every
 *   few hundred milliseconds. A level-up fires once per level.
 * - **Voice limiting.** Needed when fifteen delvers and a swarm of Rattlers can
 *   ask for forty simultaneous sounds. One-shot UI cues cannot.
 *
 * Both belong with the phase that first needs them, where their behaviour can
 * be tested against a real load instead of guessed at.
 *
 * The contract that makes incremental adoption safe is `play()`'s return value:
 * **false means "I did not make a sound"**, and every caller is expected to
 * fall back to its existing synthesis. A sample that is missing, still
 * decoding, or unsupported therefore degrades to the sound the game made
 * yesterday rather than to silence.
 */
export class SampleBank {
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly loading = new Map<string, Promise<AudioBuffer | null>>();
  /** Last take played per group, keyed by the group's first url — see `playVariant`. */
  private readonly lastVariant = new Map<string, string>();
  private disposed = false;

  constructor(
    private readonly context: AudioContext,
    private readonly destination: AudioNode,
  ) {}

  /**
   * Warm the cache. Fire-and-forget — callers should not await this, because a
   * scene must not wait on audio, and `play()` already handles "not ready yet".
   */
  preload(urls: readonly string[]): void {
    for (const url of urls) void this.load(url);
  }

  /** True once `play(url)` can actually make a sound. */
  isReady(url: string): boolean {
    return this.buffers.has(url);
  }

  /** The decoded buffer, for a caller that needs to own the node — a looping ambient bed, rather than a one-shot. */
  bufferFor(url: string): AudioBuffer | null {
    return this.buffers.get(url) ?? null;
  }

  /**
   * Play a decoded sample once.
   *
   * Returns false — having made no sound — when the buffer is not in the cache.
   * That is not an error; it is the signal for the caller's fallback. Loading is
   * kicked off on the way out, so the *next* occurrence will have it.
   */
  play(url: string, options: { gain?: number; rate?: number; pan?: number } = {}): boolean {
    if (this.disposed) return false;
    const buffer = this.buffers.get(url);
    if (!buffer) {
      void this.load(url);
      return false;
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    // Pitch variance is the caller's business: a cue that should sound
    // identical every time (a level-up) passes nothing, a cue that would betray
    // repetition (a footstep) passes a jittered rate.
    if (options.rate !== undefined) source.playbackRate.value = options.rate;

    const gain = this.context.createGain();
    gain.gain.value = options.gain ?? 1;

    // A panner is only inserted when the caller actually wants one: UI cues are
    // deliberately unspatialized, and an always-on node would be a pointless
    // allocation on the most frequently played sounds in the game.
    let tail: AudioNode = gain;
    let panner: StereoPannerNode | null = null;
    if (options.pan !== undefined && this.context.createStereoPanner) {
      panner = this.context.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, options.pan));
      gain.connect(panner);
      tail = panner;
    }
    tail.connect(this.destination);
    source.connect(gain);

    // Let go of the nodes when the sample finishes, or a long session leaks a
    // set per cue played — and footsteps play thousands of times per run.
    source.onended = () => {
      source.disconnect();
      gain.disconnect();
      panner?.disconnect();
    };
    source.start();
    return true;
  }

  /**
   * Play one of a group of interchangeable takes, never the same one twice in
   * a row.
   *
   * Random selection is wrong here: with four variants, pure random repeats
   * immediately a quarter of the time, and an immediate repeat is the single
   * most audible thing about a repeated sound — far more noticeable than the
   * fact that only four takes exist. Excluding the previous index costs
   * nothing and removes exactly that artefact.
   *
   * Returns false, having played nothing, if none of the group has decoded yet;
   * the caller falls back as with `play`.
   */
  playVariant(group: readonly string[], options: { gain?: number; rate?: number; pan?: number } = {}): boolean {
    const ready = group.filter((url) => this.buffers.has(url));
    if (ready.length === 0) {
      this.preload(group);
      return false;
    }

    const key = group[0];
    const last = this.lastVariant.get(key);
    const pool = ready.length > 1 && last !== undefined
      ? ready.filter((url) => url !== last)
      : ready;
    const chosen = pool[Math.floor(Math.random() * pool.length)];
    this.lastVariant.set(key, chosen);
    return this.play(chosen, options);
  }

  dispose(): void {
    this.disposed = true;
    this.buffers.clear();
    this.loading.clear();
    this.lastVariant.clear();
  }

  private load(url: string): Promise<AudioBuffer | null> {
    const existing = this.loading.get(url);
    if (existing) return existing;

    const pending = (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) return null;
        const decoded = await this.context.decodeAudioData(await response.arrayBuffer());
        if (!this.disposed) this.buffers.set(url, decoded);
        return decoded;
      } catch {
        // A cue we cannot load falls back to synthesis forever, which is a
        // quieter failure than any message we could show. Drop the in-flight
        // marker so a later attempt can retry.
        this.loading.delete(url);
        return null;
      }
    })();

    this.loading.set(url, pending);
    return pending;
  }
}
